import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { useFallbackStorageValue } from "~/lib/hooks";
import { createEnvironment } from "~/lib/relay";
import { checkIsInWebView, createContextValueWarningProxy, getWindow } from "~/lib/utils";
import { SignedUpLastSyncKey, SignedUpNewsletterV1LocalStorageKey } from "./newsletter/newsletter-widget/consts";
export let PageType = /*#__PURE__*/function (PageType) {
  PageType["Archive"] = "archive";
  PageType["Article"] = "article";
  PageType["Author"] = "author";
  PageType["Authors"] = "authors";
  PageType["Homepage"] = "homepage";
  PageType["Live"] = "live";
  PageType["Others"] = "others";
  PageType["Page"] = "page";
  PageType["Plus"] = "plus";
  PageType["Podcast"] = "podcast";
  PageType["Posties"] = "posties";
  PageType["PostiesArticle"] = "article_posties";
  PageType["PostiesEvent"] = "event_posties";
  PageType["PostiesEvents"] = "events_posties";
  PageType["PostiesKids"] = "content";
  PageType["PostiesParents"] = "brochure";
  PageType["PostiesSection"] = "section_posties";
  PageType["PostiesVideo"] = "video_posties";
  PageType["Section"] = "section";
  PageType["Series"] = "series";
  PageType["StoryStyle"] = "story-style";
  PageType["SubSection"] = "subsection";
  PageType["Topic"] = "topic";
  PageType["Topics"] = "topics";
  return PageType;
}({});
export const postiesPageTypes = [PageType.Posties, PageType.PostiesArticle, PageType.PostiesEvent, PageType.PostiesEvents, PageType.PostiesKids, PageType.PostiesParents, PageType.PostiesSection, PageType.PostiesVideo];
export let RouteType = /*#__PURE__*/function (RouteType) {
  RouteType["Article"] = "Article";
  RouteType["Page"] = "Page";
  RouteType["Section"] = "Section";
  RouteType["Topic"] = "Topic";
  return RouteType;
}({});
export const scmpPlus = {
  article: {
    paywallTypeId: "716f570e-3083-4138-a080-47d3830fafe3"
  },
  topicType: {
    newsAgenda: "news_agenda"
  }
};
export function parseRouteType(value) {
  if (!Object.values(RouteType).includes(value)) return;
  return value;
}
const SdkContext = /*#__PURE__*/createContext(createContextValueWarningProxy("SdkContext"));
export const SdkContextProvider = ({
  children,
  value
}) => {
  const {
    remove: removeV1SignedUpNewsletters,
    set: setV1SignedUpNewsletters,
    value: v1SignedUpNewsletters
  } = useFallbackStorageValue(SignedUpNewsletterV1LocalStorageKey, {
    defaultValue: []
  });
  const {
    remove: removeSignedUpLastSync
  } = useFallbackStorageValue(SignedUpLastSyncKey, {
    defaultValue: 0
  });
  const handleSetV1SignedUpNewslettersLocalStorage = useCallback(data => {
    // For synchronizing the local storage to v1
    setV1SignedUpNewsletters(data);
  }, [setV1SignedUpNewsletters]);
  const [popupState, setPopupState] = useState({
    acquisitions: {},
    campaigns: {},
    myNews: {},
    topicPackageFollows: {},
    whyRegister: {}
  });
  const acquisition = useMemo(() => ({
    popupState,
    setPopupState
  }), [popupState]);

  // Remove the signed up local storage when user is not login
  useEffect(() => {
    if (value.user) return;
    removeV1SignedUpNewsletters();
    removeSignedUpLastSync();
  }, [removeSignedUpLastSync, removeV1SignedUpNewsletters, value.user]);
  const currentRouteWithoutQuerystring = useMemo(() => {
    try {
      var _getWindow;
      return new URL(value.currentRoute, (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.location.origin).pathname;
    } catch {
      return "";
    }
  }, [value.currentRoute]);
  const isInWebView = useMemo(() => checkIsInWebView(), []);
  return /*#__PURE__*/React.createElement(SdkContext.Provider, {
    value: {
      ...value,
      acquisition,
      currentRouteWithoutQuerystring,
      handleSetV1SignedUpNewslettersLocalStorage,
      isInWebView,
      v1SignedUpNewsletters: v1SignedUpNewsletters !== null && v1SignedUpNewsletters !== void 0 ? v1SignedUpNewsletters : []
    },
    "data-qa": "sdk-context-provider"
  }, /*#__PURE__*/React.createElement(GoogleReCaptchaProvider, {
    reCaptchaKey: value.config.recaptcha.siteKey
  }, children));
};
SdkContextProvider.displayName = "SdkContextProvider";
export const useSdkContext = () => {
  const {
    config,
    environment,
    ...props
  } = useContext(SdkContext);
  validateUrl("facebookLikeUrl", config === null || config === void 0 ? void 0 : config.facebook.likeUrl);
  validateUrl("accountsHost", config === null || config === void 0 ? void 0 : config.accounts.host);
  validateUrl("graphqlHost", config === null || config === void 0 ? void 0 : config.graphql.host);
  validateUrl("subscribeHost", config === null || config === void 0 ? void 0 : config.subscribe.host);
  return {
    ...props,
    config,
    environment: environment !== null && environment !== void 0 ? environment : createEnvironment({
      authOptions: {
        allowEmptyToken: true
      },
      urlOptions: {
        headers: {
          apikey: config.graphql.apiKey
        },
        method: "POST",
        url: config.graphql.host
      }
    })
  };
};
const validateUrl = (title, url) => {
  if (!url) return;
  try {
    new URL(url);
  } catch {
    console.error(`${title} is not a valid url format, value ${url}`);
  }
};